import { useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { register } from '../../api/owner'
import {
  AuthError,
  AuthField,
  AuthFields,
  AuthHeading,
  AuthShell,
  AuthSubmit,
  AuthSwitch,
  authInputClass,
  authLinkClass,
} from './AuthShell'

/** Zoveel tekens moet een wachtwoord minstens hebben; de server vindt hetzelfde. */
const minimumLength = 8

/** Ronde 21B — een account maken. */
export function RegisterPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [form, setForm] = useState({ displayName: '', email: '', password: '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }))

  const complete =
    form.displayName.trim() !== '' &&
    form.email.trim() !== '' &&
    form.password.length >= minimumLength

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)

    try {
      await register(form.displayName.trim(), form.email.trim(), form.password)
      // De sessiecookie staat er nu; OwnerGate moet opnieuw kijken wie we zijn.
      await queryClient.invalidateQueries({ queryKey: ['owner'] })
      navigate('/beheer')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Het account maken lukte niet.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell>
      <form onSubmit={(event) => void submit(event)} className="mt-9 lg:mt-0">
        <AuthHeading title="Maak een account">Eén account, zoveel feesten als je wil.</AuthHeading>

        <AuthFields>
          <AuthField id="naam" label="Naam">
            <input
              id="naam"
              autoComplete="name"
              autoFocus
              placeholder="Lotte Vermeulen"
              value={form.displayName}
              onChange={(event) => set('displayName')(event.target.value)}
              className={authInputClass}
            />
          </AuthField>

          <AuthField id="email" label="E-mail">
            <input
              id="email"
              type="email"
              autoComplete="username"
              placeholder="lotte@voorbeeld.be"
              value={form.email}
              onChange={(event) => set('email')(event.target.value)}
              className={authInputClass}
            />
          </AuthField>

          <AuthField id="password" label="Wachtwoord">
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              placeholder={`Minstens ${minimumLength} tekens`}
              value={form.password}
              onChange={(event) => set('password')(event.target.value)}
              className={authInputClass}
            />
          </AuthField>

          <AuthSubmit busy={busy} disabled={!complete}>
            Account maken
          </AuthSubmit>
        </AuthFields>

        {error && <AuthError>{error}</AuthError>}

        <p className="font-brand text-brand-faint mt-5 text-center text-[13px] leading-[1.5] text-pretty">
          Door verder te gaan ga je akkoord met de voorwaarden en het privacybeleid.
        </p>

        <AuthSwitch question="Al een account?">
          <Link to="/beheer/aanmelden" className={authLinkClass}>
            Aanmelden
          </Link>
        </AuthSwitch>
      </form>
    </AuthShell>
  )
}
